/**
 * DOMI-CARE - Módulo 2: Historia Clínica Domiciliaria (HCD) Interdisciplinaria
 * Registro en tiempo real de evoluciones, PAM, signos vitales, CIE-10 y doble firma digital.
 */

import { offlineStore } from '../../api/offlineStore.js';
import { appsScriptClient } from '../../api/appsScriptClient.js';
import { authState } from '../../state/authState.js';
import { renderVitalSignsGrid } from '../../components/vitalSignsWidget.js';
import { ModalManager } from '../../components/modal.js';
import { SignaturePad } from '../../components/signaturePad.js';
import { notify } from '../../components/notificationCenter.js';
import { buscarCIE10 } from '../../data/cie10Data.js';

let sigPadProf = null;
let sigPadFam = null;

export async function renderHCDView(container, activePatientId) {
  const paciente = await offlineStore.getById('pacientes', activePatientId);
  if (!paciente) {
    container.innerHTML = `<div class="card" style="padding: 2rem; text-align: center;">Por favor selecciona un paciente en el padrón.</div>`;
    return;
  }

  const allEvoluciones = await offlineStore.getAll('evoluciones');
  const evoluciones = allEvoluciones.filter(e => e.patientId === activePatientId)
    .sort((a, b) => new Date(b.fechaHora) - new Date(a.fechaHora));

  const allSignos = await offlineStore.getAll('signos');
  const signosPaciente = allSignos.filter(s => s.patientId === activePatientId)
    .sort((a, b) => new Date(b.fechaHora) - new Date(a.fechaHora));
  const latestSignos = signosPaciente[0] || null;

  const user = authState.getActiveUser();
  const canEdit = authState.hasPermission('canEditHCD');

  container.innerHTML = `
    <div class="view-header">
      <div>
        <h1 class="view-title">Historia Clínica Domiciliaria (HCD)</h1>
        <p class="view-subtitle">Expediente interdisciplinario, constantes vitales y evoluciones en domicilio</p>
      </div>
      <div class="view-actions">
        ${canEdit ? `
          <button id="btn-nueva-evolucion" class="btn btn-primary">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Registrar Evolución / Práctica
          </button>
        ` : `
          <span class="badge badge-slate">Modo Lectura (Rol: ${user.rol})</span>
        `}
      </div>
    </div>

    <!-- Banner del Paciente y Diagnóstico CIE-10 -->
    <div class="patient-banner">
      <div>
        <div style="font-size: 1.35rem; font-weight: 800; display: flex; align-items: center; gap: 0.75rem;">
          ${paciente.nombreCompleto}
          <span class="badge badge-primary" style="background: rgba(255,255,255,0.25); color: #ffffff;">${paciente.nivelComplejidad}</span>
        </div>
        <div style="font-size: 0.85rem; opacity: 0.9; margin-top: 0.3rem;">
          DNI: ${paciente.dni} • ${paciente.edad || 70} años • Cobertura: ${paciente.obraSocial} (N° Afiliado: ${paciente.numeroAfiliado || 'S/D'})
        </div>
      </div>
      <div style="text-align: right; background: rgba(0,0,0,0.2); padding: 0.6rem 1rem; border-radius: var(--radius-md);">
        <div style="font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em; opacity: 0.8;">Diagnóstico Principal CIE-10</div>
        <div style="font-weight: 700; font-size: 0.95rem;">${paciente.diagnosticoPrincipal?.code} - ${paciente.diagnosticoPrincipal?.desc}</div>
      </div>
    </div>

    <!-- Sección de Constantes Vitales Actuales -->
    <div style="margin-bottom: 1.5rem;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.6rem;">
        <h3 style="font-size: 1rem; font-weight: 700; color: var(--text-primary);">📊 Monitor de Signos Vitales (Último Registro)</h3>
        <span style="font-size: 0.75rem; color: var(--text-muted);">${latestSignos ? `Controlado: ${latestSignos.fechaHora}` : ''}</span>
      </div>
      ${renderVitalSignsGrid(latestSignos)}
    </div>

    <!-- Plan de Atención Médica (PAM) -->
    <div class="card" style="margin-bottom: 1.5rem; background: linear-gradient(135deg, #ffffff, #f0fdfa); border-left: 4px solid var(--teal-600);">
      <div class="card-header" style="margin-bottom: 0.6rem; padding-bottom: 0.4rem;">
        <h3 class="card-title" style="color: var(--teal-700);">
          🎯 Plan de Atención Médica Domiciliaria (PAM)
        </h3>
        <span class="badge badge-success">Protocolo Activo</span>
      </div>
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1rem; font-size: 0.875rem;">
        <div>
          <div style="font-weight: 700; color: var(--text-secondary); margin-bottom: 0.2rem;">Objetivos del Tratamiento:</div>
          <p style="color: var(--text-primary);">${paciente.pam?.objetivos || 'Plan en elaboración'}</p>
        </div>
        <div>
          <div style="font-weight: 700; color: var(--text-secondary); margin-bottom: 0.2rem;">Frecuencia de Prestaciones:</div>
          <p style="color: var(--text-primary);">${paciente.pam?.frecuenciaVisitas || 'Según necesidad médica'}</p>
        </div>
      </div>
    </div>

    <!-- Timeline de Evoluciones Clínicas Interdisciplinarias -->
    <div>
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
        <h3 style="font-size: 1.1rem; font-weight: 800; color: var(--text-primary);">
          📝 Registro de Evoluciones Clínicas y Prácticas (${evoluciones.length})
        </h3>
      </div>

      <div style="display: flex; flex-direction: column; gap: 1.25rem;">
        ${evoluciones.length === 0 ? `
          <div class="card" style="text-align: center; color: var(--text-muted); padding: 2rem;">
            No hay evoluciones clínicas registradas aún. Presiona "Registrar Evolución" para añadir la primera visita.
          </div>
        ` : evoluciones.map(evo => renderEvolucionCard(evo)).join('')}
      </div>
    </div>

    <!-- Modal para Nueva Evolución -->
    ${renderModalNuevaEvolucion(paciente, user)}
  `;

  // Init Signature Pads al abrir el modal
  if (canEdit) {
    const btnNew = document.getElementById('btn-nueva-evolucion');
    if (btnNew) {
      btnNew.addEventListener('click', () => {
        ModalManager.open('modal-nueva-evolucion');
        setTimeout(initSignaturePads, 250);
      });
    }

    // Buscador interactivo CIE-10
    const inputCie10 = document.getElementById('search-cie10');
    const listCie10 = document.getElementById('cie10-results');
    if (inputCie10 && listCie10) {
      inputCie10.addEventListener('input', (e) => {
        const query = e.target.value;
        const matches = buscarCIE10(query);
        listCie10.innerHTML = matches.map(m => `
          <div class="cie10-item" data-code="${m.code}" data-desc="${m.desc}" style="padding: 0.4rem 0.6rem; cursor: pointer; border-bottom: 1px solid var(--border-subtle); font-size: 0.8rem;">
            <strong style="color: var(--primary-700);">${m.code}</strong> - ${m.desc}
          </div>
        `).join('');
        listCie10.style.display = matches.length ? 'block' : 'none';

        listCie10.querySelectorAll('.cie10-item').forEach(it => {
          it.addEventListener('click', (ev) => {
            const code = ev.currentTarget.dataset.code;
            const desc = ev.currentTarget.dataset.desc;
            inputCie10.value = `${code} - ${desc}`;
            listCie10.style.display = 'none';
          });
        });
      });
    }

    // Botón GPS
    const btnGps = document.getElementById('btn-capturar-gps');
    if (btnGps) {
      btnGps.addEventListener('click', () => {
        if (navigator.geolocation) {
          btnGps.innerHTML = '⌛ Obteniendo GPS...';
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const coords = `${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`;
              document.getElementById('input-gps').value = coords;
              btnGps.innerHTML = '✓ GPS Capturado';
              notify.success(`Coordenadas registradas: ${coords}`);
            },
            (err) => {
              // Fallback con mock de Buenos Aires
              document.getElementById('input-gps').value = '-34.6231, -58.4489 (Ubicación de Referencia)';
              btnGps.innerHTML = '✓ GPS Aproximado';
              notify.warning('No se pudo acceder al GPS del dispositivo, usando coordenadas del domicilio.');
            }
          );
        }
      });
    }

    // Form submission
    const form = document.getElementById('form-nueva-evolucion');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        await handleSaveEvolucionForm(form, paciente, user, container, onSelectPatientRefresh);
      });
    }
  }

  function onSelectPatientRefresh() {
    renderHCDView(container, activePatientId);
  }
}

function getSafeSignatureSrc(src) {
  if (!src) return '';
  if (typeof src === 'string' && src.startsWith('data:image/svg+xml;utf8,<')) {
    const rawSvg = src.replace('data:image/svg+xml;utf8,', '');
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(rawSvg);
  }
  return src;
}

function renderEvolucionCard(evo) {
  const sigProfSrc = getSafeSignatureSrc(evo.firmas?.profesional);
  const sigFamSrc = getSafeSignatureSrc(evo.firmas?.familiar);

  return `
    <div class="card" style="border-left: 4px solid var(--primary-600);">
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.75rem; flex-wrap: wrap; gap: 0.5rem;">
        <div>
          <span class="badge badge-primary" style="margin-right: 0.5rem;">${evo.disciplina}</span>
          <strong style="font-size: 0.95rem; color: var(--text-primary);">${evo.prestadorNombre}</strong>
          <span style="font-size: 0.8rem; color: var(--text-muted); margin-left: 0.3rem;">(Mat. ${evo.matricula || 'MN'})</span>
        </div>
        <div style="font-size: 0.8rem; color: var(--text-muted); font-family: var(--font-mono);">
          🗓️ ${evo.fechaHora} • 📍 ${evo.gps || 'Domicilio'}
        </div>
      </div>

      <div style="font-size: 0.875rem; display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem;">
        <div>
          <span style="font-weight: 700; color: var(--text-secondary);">Subjetivo / Examen Físico: </span>
          <span>${evo.subjetivoObjetivo}</span>
        </div>
        <div>
          <span style="font-weight: 700; color: var(--text-secondary);">Evaluación Diagnóstica: </span>
          <span style="color: var(--primary-900); font-weight: 600;">${evo.evaluacionDiagnostica}</span>
        </div>
        <div>
          <span style="font-weight: 700; color: var(--text-secondary);">Plan Terapéutico y Prácticas: </span>
          <span>${evo.planTratamiento}</span>
        </div>
        ${evo.practicasRealizadas ? `
          <div style="background: var(--slate-50); padding: 0.5rem 0.75rem; border-radius: var(--radius-sm); border: 1px solid var(--border-subtle);">
            <strong>Prácticas de Enfermería/Kine: </strong>${evo.practicasRealizadas}
          </div>
        ` : ''}
      </div>

      <!-- Previsualización de Doble Firma -->
      <div style="display: flex; gap: 1rem; border-top: 1px solid var(--border-subtle); padding-top: 0.75rem; flex-wrap: wrap;">
        <div style="flex: 1; min-width: 200px; background: #fafafa; border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); padding: 0.5rem; text-align: center;">
          <div style="font-size: 0.7rem; text-transform: uppercase; font-weight: 700; color: var(--text-muted); margin-bottom: 0.3rem;">Firma del Profesional</div>
          ${sigProfSrc ? `
            <img src="${sigProfSrc}" alt="Firma Profesional" style="max-height: 48px; max-width: 100%; margin: 0 auto; display: block;">
          ` : '<span style="font-size: 0.75rem; color: var(--text-muted);">Firma registrada digitalmente</span>'}
          <div style="font-size: 0.72rem; color: var(--text-primary); font-weight: 600;">${evo.prestadorNombre}</div>
        </div>

        <div style="flex: 1; min-width: 200px; background: #fafafa; border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); padding: 0.5rem; text-align: center;">
          <div style="font-size: 0.7rem; text-transform: uppercase; font-weight: 700; color: var(--text-muted); margin-bottom: 0.3rem;">Firma de Conformidad Familiar/Paciente</div>
          ${sigFamSrc ? `
            <img src="${sigFamSrc}" alt="Firma Familiar" style="max-height: 48px; max-width: 100%; margin: 0 auto; display: block;">
          ` : '<span style="font-size: 0.75rem; color: var(--text-muted);">Firma de conformidad registrada</span>'}
          <div style="font-size: 0.72rem; color: var(--text-primary); font-weight: 600;">${evo.firmas?.aclaracionFamiliar || 'Familiar a cargo'}</div>
        </div>
      </div>
    </div>
  `;
}

function renderModalNuevaEvolucion(paciente, user) {
  return `
    <div id="modal-nueva-evolucion" class="modal-overlay">
      <div class="modal-container" style="max-width: 800px;">
        <div class="modal-header">
          <h2 style="font-size: 1.15rem; font-weight: 700; color: var(--text-primary); display: flex; align-items: center; gap: 0.5rem;">
            📝 Registro de Evolución Domiciliaria en Campo
          </h2>
          <button class="btn btn-secondary btn-sm" onclick="document.getElementById('modal-nueva-evolucion').classList.remove('active')">✕</button>
        </div>
        <form id="form-nueva-evolucion" style="display: flex; flex-direction: column; flex: 1; min-height: 0; overflow: hidden;">
          <div class="modal-body" style="display: flex; flex-direction: column; gap: 1rem; overflow-y: auto; max-height: calc(88vh - 130px); -webkit-overflow-scrolling: touch; padding: 1.25rem 1.5rem;">
            
            <!-- Profesional y Paciente Info -->
            <div style="background: var(--slate-50); padding: 0.75rem; border-radius: var(--radius-md); font-size: 0.825rem; display: flex; justify-content: space-between; flex-wrap: wrap; gap: 0.5rem; border: 1px solid var(--border-subtle);">
              <div><strong>Paciente:</strong> ${paciente.nombreCompleto} (DNI ${paciente.dni})</div>
              <div><strong>Profesional:</strong> ${user.nombre} (${user.rol})</div>
            </div>

            <!-- Diagnóstico CIE-10 Predictivo -->
            <div class="form-group" style="position: relative;">
              <label class="form-label">Diagnóstico Clínico / CIE-10 *</label>
              <input type="text" id="search-cie10" name="evaluacionDiagnostica" class="form-control" required placeholder="Escribe para buscar código CIE-10 (ej: ACV, EPOC, Úlcera, Fractura...)" autocomplete="off">
              <div id="cie10-results" style="display: none; position: absolute; top: 100%; left: 0; right: 0; background: #ffffff; border: 1px solid var(--border-medium); border-radius: var(--radius-md); box-shadow: var(--shadow-lg); z-index: 50; max-height: 180px; overflow-y: auto;"></div>
            </div>

            <!-- Evolución Subjetiva y Objetiva -->
            <div class="form-group">
              <label class="form-label">Examen Físico y Estado Clínico Actual (Subjetivo / Objetivo) *</label>
              <textarea name="subjetivoObjetivo" class="form-control" rows="2" required placeholder="Detalla estado de lucidez, mecánica respiratoria, examen cardiopulmonar, abdomen, estado de la piel o marcha..."></textarea>
            </div>

            <!-- Plan y Prácticas Realizadas -->
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Plan Terapéutico y Conducta *</label>
                <textarea name="planTratamiento" class="form-control" rows="2" required placeholder="Indicaciones, ajuste de tratamiento, pautas para la familia..."></textarea>
              </div>
              <div class="form-group">
                <label class="form-label">Prácticas Realizadas (Curaciones / Kine / Vías)</label>
                <textarea name="practicasRealizadas" class="form-control" rows="2" placeholder="Ej: Curación plana con hidrocoloide, aspiración de secreciones, movilización de cadera..."></textarea>
              </div>
            </div>

            <!-- Constantes Vitales en Visita (Opcional pero recomendado) -->
            <div>
              <label class="form-label" style="margin-bottom: 0.4rem;">Constantes Vitales Tomadas en la Visita</label>
              <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 0.5rem;">
                <input type="number" name="sistolica" class="form-control" placeholder="TAS (120)">
                <input type="number" name="diastolica" class="form-control" placeholder="TAD (80)">
                <input type="number" name="fc" class="form-control" placeholder="FC (lpm)">
                <input type="number" name="spo2" class="form-control" placeholder="SatO2 (%)">
                <input type="number" step="0.1" name="temp" class="form-control" placeholder="Temp °C">
                <input type="number" name="glu" class="form-control" placeholder="Glucemia">
              </div>
            </div>

            <!-- Captura de Geolocalización GPS -->
            <div style="display: flex; gap: 0.75rem; align-items: center;">
              <input type="text" id="input-gps" name="gps" class="form-control" placeholder="Coordenadas GPS de la visita" style="font-family: var(--font-mono); font-size: 0.8rem;" readonly>
              <button type="button" id="btn-capturar-gps" class="btn btn-secondary btn-sm" style="flex-shrink: 0;">
                📍 Capturar GPS Ahora
              </button>
            </div>

            <!-- MÓDULO DE DOBLE FIRMA DIGITAL TÁCTIL -->
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 1rem; border-top: 1px solid var(--border-subtle); padding-top: 0.75rem;">
              <!-- Firma Profesional -->
              <div>
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.2rem;">
                  <label class="form-label">Firma del Profesional *</label>
                  <button type="button" class="btn btn-secondary btn-sm" style="padding: 0.15rem 0.4rem; font-size: 0.7rem;" onclick="window.clearSigProf()">Borrar</button>
                </div>
                <div class="signature-box">
                  <canvas id="canvas-firma-prof" class="signature-canvas"></canvas>
                  <div class="signature-actions">
                    <span>Firme dentro del recuadro</span>
                    <span>${user.nombre}</span>
                  </div>
                </div>
              </div>

              <!-- Firma de Conformidad Familiar -->
              <div>
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.2rem;">
                  <label class="form-label">Firma de Conformidad (Familiar / Paciente) *</label>
                  <button type="button" class="btn btn-secondary btn-sm" style="padding: 0.15rem 0.4rem; font-size: 0.7rem;" onclick="window.clearSigFam()">Borrar</button>
                </div>
                <div class="signature-box">
                  <canvas id="canvas-firma-fam" class="signature-canvas"></canvas>
                  <div class="signature-actions">
                    <span>Firme para confirmar visita</span>
                    <span>Conformidad Domicilio</span>
                  </div>
                </div>
                <input type="text" name="aclaracionFamiliar" class="form-control" style="margin-top: 0.4rem; font-size: 0.8rem;" placeholder="Aclaración y DNI del familiar/cuidador" value="${paciente.accesibilidad?.cuidadorNombre ? paciente.accesibilidad.cuidadorNombre + ' (' + paciente.accesibilidad.cuidadorVinculo + ')' : ''}">
              </div>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-nueva-evolucion').classList.remove('active')">Cancelar</button>
            <button type="submit" class="btn btn-primary">Guardar Evolución y Transmitir</button>
          </div>
        </form>
      </div>
    </div>
  `;
}

function initSignaturePads() {
  const canvasProf = document.getElementById('canvas-firma-prof');
  const canvasFam = document.getElementById('canvas-firma-fam');

  if (canvasProf) {
    sigPadProf = new SignaturePad(canvasProf, { strokeColor: '#0284c7', lineWidth: 2.5 });
  }
  if (canvasFam) {
    sigPadFam = new SignaturePad(canvasFam, { strokeColor: '#1e293b', lineWidth: 2.5 });
  }

  window.clearSigProf = () => sigPadProf?.clear();
  window.clearSigFam = () => sigPadFam?.clear();
}

async function handleSaveEvolucionForm(form, paciente, user, container, onRefresh) {
  const formData = new FormData(form);

  const firmaProf = sigPadProf ? sigPadProf.toDataURL() : null;
  const firmaFam = sigPadFam ? sigPadFam.toDataURL() : null;

  const evolucionData = {
    patientId: paciente.id,
    subjetivoObjetivo: formData.get('subjetivoObjetivo'),
    evaluacionDiagnostica: formData.get('evaluacionDiagnostica'),
    planTratamiento: formData.get('planTratamiento'),
    practicasRealizadas: formData.get('practicasRealizadas'),
    firmaProfesional: firmaProf,
    firmaFamiliar: firmaFam,
    aclaracionFamiliar: formData.get('aclaracionFamiliar'),
    gps: formData.get('gps') || 'Registrado en campo'
  };

  const signosData = {
    sistolica: formData.get('sistolica'),
    diastolica: formData.get('diastolica'),
    frecuenciaCardiaca: formData.get('fc'),
    saturacionO2: formData.get('spo2'),
    temperatura: formData.get('temp'),
    glucemia: formData.get('glu')
  };

  const res = await appsScriptClient.saveEvolucion(evolucionData, signosData, null, user);

  ModalManager.close('modal-nueva-evolucion');
  if (res.synced) {
    notify.success('Evolución registrada y sincronizada con Google Sheets exitosamente.');
  } else {
    notify.warning('Evolución guardada localmente (Offline). Se sincronizará al detectar conexión.');
  }

  onRefresh();
}
